import { useCallback, useEffect, useRef } from 'react';
import type { BombiixRowId, OnRowChange } from './types';

interface PendingCommit {
  rowId: BombiixRowId;
  field: string;
  value: unknown;
}

export type CommitFn = (rowId: BombiixRowId, field: string, value: unknown) => void;
export type FlushFn = () => void;

/**
 * Decouples an input's own paint from the parent's recalc pipeline. Keeps the
 * local update urgent and commits to the parent on the next animation frame,
 * coalescing a burst of keystrokes into a single commit. `flush` runs any
 * pending commit immediately — call it on blur so leaving the field never
 * strands an un-committed value.
 */
function useDeferredRowChange(onRowChange: OnRowChange | undefined): [CommitFn, FlushFn] {
  const frameRef = useRef<number>(0);
  const pendingRef = useRef<PendingCommit | null>(null);
  const onRowChangeRef = useRef(onRowChange);
  onRowChangeRef.current = onRowChange;

  const run = useCallback(() => {
    frameRef.current = 0;
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (pending) onRowChangeRef.current?.(pending.rowId, pending.field, pending.value);
  }, []);

  const commit = useCallback<CommitFn>(
    (rowId, field, value) => {
      pendingRef.current = { rowId, field, value };
      if (frameRef.current) return;
      frameRef.current = typeof requestAnimationFrame === 'function' ? requestAnimationFrame(run) : 0;
      if (!frameRef.current) run();
    },
    [run],
  );

  const flush = useCallback<FlushFn>(() => {
    if (frameRef.current && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(frameRef.current);
    frameRef.current = 0;
    run();
  }, [run]);

  useEffect(
    () => () => {
      if (frameRef.current && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(frameRef.current);
    },
    [],
  );

  return [commit, flush];
}

export default useDeferredRowChange;
